/**
 * Minimal user/session payload for Self-Learn (aligned with `@repo/self-learn-app`).
 * Host apps map auth/profile/enrollment into {@link ModuleDataDto}; use
 * {@link moduleDataDtoToSelfLearnSession} in `setupApi` to derive this shape for headers.
 */
export interface SelfLearnSession {
  accessToken: string | null;
  refreshToken?: string | null;
  gradeId: number | null;
  mdsGradeId: number | null;
  examId: number | null;
  examName: string | null;
  mdsExamId: number | null;
  mdsExamName: string | null;
  tenantId: string | null;
  subtenantId: number | null;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  uamId: number | null;
  verticalId: number | null;
  unifiedAccountId: string | null;
  programId?: string | null;
  /** Optional copy of enrolled programs for legacy screens; prefer flat fields above */
  enrolledPrograms?: unknown[] | null;
  setSessionData: (tenantId: string | null, subtenantId: number | null) => void;
}

/**
 * Placeholder only — **not** wired to any auth store (`@repo/self-learn-web` must not import app stores).
 * Runtime headers use `moduleDataDtoToSelfLearnSession` + host `ModuleDataDto` inside `initializeApi`.
 * Example: `oaStudentWeb` `getModuleDataDto()` reads `useAuthStore.getState()` and passes the result as `dataDto`.
 */
export const defaultSelfLearnSession: SelfLearnSession = {
  accessToken: null,
  refreshToken: null,
  gradeId: null,
  mdsGradeId: null,
  examId: null,
  examName: null,
  mdsExamId: null,
  mdsExamName: null,
  tenantId: null,
  subtenantId: null,
  firstName: null,
  lastName: null,
  phone: null,
  uamId: null,
  verticalId: null,
  unifiedAccountId: null,
  programId: null,
  enrolledPrograms: null,
  setSessionData: () => {},
};

/** Program-shaped snapshot from {@link defaultSelfLearnSession} (set via `initializeApi` from auth `enrolledPrograms[0]`). */
export function getSessionProgramForUi(): {
  program_id?: string;
  gradeId?: number;
  examId?: number;
  tenant_id?: string;
  subtenant_id?: number;
  examName?: string | null;
} | null {
  const s = defaultSelfLearnSession;
  if (s.programId == null) return null;
  return {
    program_id: s.programId,
    gradeId: s.gradeId ?? undefined,
    examId: s.examId ?? undefined,
    tenant_id: s.tenantId ?? undefined,
    subtenant_id: s.subtenantId ?? undefined,
    examName: s.examName,
  };
}

/** Tenant / grade / exam / token fields for player pages — reads {@link defaultSelfLearnSession} only. */
export function readSelfLearnWebAuthFromSession(): {
  accessToken: string;
  userId: string;
  tenantId: string;
  subtenantId: string;
  examId: number;
  gradeId: number;
} {
  const s = defaultSelfLearnSession;
  return {
    accessToken: s.accessToken ?? "",
    userId: s.uamId != null ? String(s.uamId) : "",
    tenantId: s.tenantId ?? "",
    subtenantId: s.subtenantId != null ? String(s.subtenantId) : "",
    examId: s.examId ?? 0,
    gradeId: s.gradeId ?? 0,
  };
}
